import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Email history + open tracking (CLE-310). Read-only admin views over the
// EmailLog audit trail the backend mailer populates on every send.

export type EmailStatus = 'SENT' | 'FAILED';

export interface EmailLog {
  id: string;
  to: string;
  subject: string;
  template: string;
  status: EmailStatus;
  sentAt: string;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  metadata: Record<string, unknown> | null;
  trackingId: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface EmailListResponse {
  success: true;
  data: EmailLog[];
  meta: EmailListMeta;
}

export interface EmailTemplateStat {
  template: string;
  sent: number;
  opened: number;
  openRate: number;
}

export interface EmailStats {
  totalSent: number;
  totalOpened: number;
  openRate: number;
  byTemplate: EmailTemplateStat[];
}

interface Envelope<T> {
  success: true;
  data: T;
}

export interface EmailFilters {
  template?: string;
  // Recipient substring filter (case-insensitive).
  to?: string;
  status?: EmailStatus;
  // sentAt range. `from` is the start; `dateTo` is the end (the API reserves
  // `to` for the recipient filter above).
  from?: string;
  dateTo?: string;
  sort?: 'sentAt' | 'openCount';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Authorization header built from the persisted access token.
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: EmailFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listEmails(filters: EmailFilters = {}): Promise<EmailListResponse> {
  return apiFetch<EmailListResponse>(`/api/admin/emails${buildQuery(filters)}`, {
    headers: authHeaders(),
  });
}

export async function getEmailStats(): Promise<EmailStats> {
  const res = await apiFetch<Envelope<EmailStats>>('/api/admin/emails/stats', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getEmail(id: string): Promise<EmailLog> {
  const res = await apiFetch<Envelope<EmailLog>>(`/api/admin/emails/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}
